import { ArrowRight, KeyRound, LockKeyhole, ShieldCheck } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { BrandLogo } from "../../components/BrandLogo";
import { ThemeToggle } from "../../components/ThemeToggle";
import { useSession } from "../../context/SessionContext";

function StaffLogin() {
  const { status, user, loginStaff } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [useRecoveryCode, setUseRecoveryCode] = useState(false);
  const [step, setStep] = useState<"credentials" | "mfa">("credentials");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (status === "authenticated" && user?.kind === "staff") {
      const destination = user.mfaSetupRequired
        ? "/agent/settings?enroll-mfa=1"
        : user.passwordChangeRequired
          ? "/agent/settings?change-password=1"
        : (location.state as { from?: string } | null)?.from ?? "/agent";
      navigate(destination, { replace: true });
    }
  }, [location.state, navigate, status, user]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await loginStaff({
        email: email.trim(),
        password,
        mfaCode: useRecoveryCode ? undefined : mfaCode || undefined,
        recoveryCode: useRecoveryCode ? recoveryCode.trim() || undefined : undefined,
      });
      if (result.requiresMfa) {
        setError(null);
        setStep("mfa");
        return;
      }
      navigate(
        result.user?.mfaSetupRequired
          ? "/agent/settings?enroll-mfa=1"
          : result.user?.passwordChangeRequired
            ? "/agent/settings?change-password=1"
          : "/agent",
        { replace: true },
      );
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Sign-in failed.";
      setError(message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="staff-login-page">
      <div className="staff-login-page__visual" aria-hidden="true">
        <div className="login-orb login-orb--one" /><div className="login-orb login-orb--two" />
        <div className="login-quote">
          <span><ShieldCheck size={22} /></span>
          <p>One workspace for customer context, migration details, and every support conversation.</p>
          <small>Next-Cart Support</small>
        </div>
      </div>
      <section className="staff-login-panel">
        <header className="staff-login-panel__header"><BrandLogo /><ThemeToggle compact /></header>
        <div className="staff-login-form-wrap">
          <span className="login-icon"><LockKeyhole aria-hidden="true" /></span>
          <p className="eyebrow">Support workspace</p>
          <h1>{step === "mfa" ? "Verify it’s you" : "Welcome back"}</h1>
          <p>{step === "mfa"
            ? useRecoveryCode
              ? "Enter one of the one-time recovery codes saved during enrollment."
              : "Enter the 6-digit code from your authenticator app."
            : "Use your staff account. Customer SSO credentials do not work here."}</p>
          {error && <div className="form-alert" role="alert">{error}</div>}
          <form onSubmit={submit}>
            {step === "credentials" ? (
              <>
                <label className="field"><span>Work email</span><input type="email" autoComplete="username" value={email} onChange={(event) => setEmail(event.target.value)} required autoFocus placeholder="you@next-cart.com" /></label>
                <label className="field"><span>Password</span><input type="password" autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} required minLength={8} /></label>
              </>
            ) : useRecoveryCode ? (
              <label className="field mfa-field">
                <span>Recovery code</span>
                <div>
                  <KeyRound aria-hidden="true" size={18} />
                  <input
                    type="text"
                    autoComplete="one-time-code"
                    minLength={8}
                    maxLength={100}
                    value={recoveryCode}
                    onChange={(event) => setRecoveryCode(event.target.value.toUpperCase())}
                    required
                    autoFocus
                    placeholder="XXXX-XXXX-XXXX"
                  />
                </div>
              </label>
            ) : (
              <label className="field mfa-field"><span>Authentication code</span><div><KeyRound aria-hidden="true" size={18} /><input inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6,8}" maxLength={8} value={mfaCode} onChange={(event) => setMfaCode(event.target.value.replace(/\D/g, ""))} required autoFocus placeholder="000000" /></div></label>
            )}
            <button className="button button--primary button--full" type="submit" disabled={busy}>{busy ? "Checking…" : step === "mfa" ? "Verify and continue" : "Sign in"}<ArrowRight aria-hidden="true" size={17} /></button>
            {step === "mfa" && (
              <>
                <button className="button button--secondary button--full" type="button" onClick={() => { setUseRecoveryCode((value) => !value); setError(null); }}>
                  {useRecoveryCode ? "Use authenticator code" : "Use a recovery code"}
                </button>
                <button className="button button--quiet button--full" type="button" onClick={() => { setStep("credentials"); setPassword(""); setMfaCode(""); setRecoveryCode(""); setUseRecoveryCode(false); setError(null); }}>Back to sign in</button>
              </>
            )}
          </form>
          <p className="login-help">Having trouble? Contact your support administrator.</p>
        </div>
        <footer><span>Protected by MFA and secure session controls.</span><Link to="/">Customer support portal</Link></footer>
      </section>
    </main>
  );
}

export function AgentLoginPage() {
  return <StaffLogin />;
}
